import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowLeft, Loader2, TrendingDown, ThumbsUp, ThumbsDown, AlertCircle, ChevronDown, ChevronRight, DollarSign, BarChart3, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';
import ToolSalesGate from '../components/ToolSalesGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealAnalyzerPage = (props) => {
  const params = useParams();
  const sessionId = props.sessionId ?? params.sessionId;
  if (props.skipGate) {
    return <DealAnalyzerContent sessionId={sessionId} backTo={props.backTo} backLabel={props.backLabel} />;
  }
  return (
    <ToolSalesGate sessionId={sessionId} toolName="deal_analyzer">
      <DealAnalyzerContent sessionId={sessionId} />
    </ToolSalesGate>
  );
};

const verdictConfig = {
  'STEAL': { color: '#10b981', bg: '#10b98118', label: 'STEAL', icon: '🎯' },
  'GOOD_DEAL': { color: '#22c55e', bg: '#22c55e18', label: 'GOOD DEAL', icon: '👍' },
  'FAIR_PRICE': { color: '#f59e0b', bg: '#f59e0b18', label: 'FAIR PRICE', icon: '⚖️' },
  'OVERPRICED': { color: '#ef4444', bg: '#ef444418', label: 'OVERPRICED', icon: '⚠️' },
  'RIP_OFF': { color: '#dc2626', bg: '#dc262618', label: 'RIP OFF', icon: '🚫' },
};

const recConfig = {
  'BUY': { color: '#22c55e', label: 'BUY IT' },
  'NEGOTIATE': { color: '#f59e0b', label: 'NEGOTIATE' },
  'PASS': { color: '#ef4444', label: 'PASS' },
};

const DealAnalyzerContent = ({ sessionId, backTo, backLabel }) => {
  const { isDark } = useTheme();
  const [form, setForm] = useState({
    rv_year: '', rv_brand: '', rv_model: '', asking_price: '',
    condition: 'used', mileage: '', length_ft: '', features: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState('summary');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rv_year || !form.rv_brand || !form.rv_model || !form.asking_price) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        rv_year: parseInt(form.rv_year), rv_brand: form.rv_brand, rv_model: form.rv_model,
        asking_price: parseFloat(form.asking_price), condition: form.condition,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        length_ft: form.length_ft ? parseInt(form.length_ft) : null,
        features: form.features || null,
      };
      const res = await axios.post(`${API}/tools/deal-analyzer`, payload);
      setResult(res.data.analysis);
    } catch {
      toast.error('Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#ec4899';

  const v = result?.verdict ? verdictConfig[result.verdict] || verdictConfig['FAIR_PRICE'] : null;
  const r = result?.recommendation ? recConfig[result.recommendation] || recConfig['NEGOTIATE'] : null;

  return (
    <div data-testid="deal-analyzer-page" style={{ background: bg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ background: isDark ? '#101018' : '#fff', borderBottom: `1px solid ${border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to={backTo || (sessionId ? `/vip/${sessionId}/dashboard` : '/')} style={{ color: textSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={18} /> {backLabel || (sessionId ? 'VIP Dashboard' : 'Home')}
          </Link>
          <span style={{ color: textSecondary }}>/</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={18} style={{ color: accent }} />
            <span style={{ color: accent, fontWeight: 700, fontSize: 15 }}>Deal Analyzer</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `${accent}15`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: accent, marginBottom: 12 }}>
            <Search size={28} />
          </div>
          <h1 style={{ color: textPrimary, fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em' }}>AI Deal Analyzer</h1>
          <p style={{ color: textSecondary, fontSize: 15, maxWidth: 500, margin: '8px auto 0' }}>
            Is that RV a steal or a rip-off? Get an instant AI assessment.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: result ? '380px 1fr' : '1fr', gap: 24, alignItems: 'start' }}>
          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="deal-analyzer-form" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Year *</Label>
                <Input data-testid="deal-year" type="number" value={form.rv_year} onChange={e => setForm(f => ({...f, rv_year: e.target.value}))} placeholder="2021" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Brand *</Label>
                <Input data-testid="deal-brand" value={form.rv_brand} onChange={e => setForm(f => ({...f, rv_brand: e.target.value}))} placeholder="Winnebago" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Model *</Label>
                <Input data-testid="deal-model" value={form.rv_model} onChange={e => setForm(f => ({...f, rv_model: e.target.value}))} placeholder="View 24D" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Asking Price *</Label>
                <Input data-testid="deal-price" type="number" value={form.asking_price} onChange={e => setForm(f => ({...f, asking_price: e.target.value}))} placeholder="65000" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Mileage</Label>
                <Input data-testid="deal-mileage" type="number" value={form.mileage} onChange={e => setForm(f => ({...f, mileage: e.target.value}))} placeholder="18000" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Condition</Label>
                <select data-testid="deal-condition" value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                  <option value="new">New</option><option value="like_new">Like New</option><option value="used">Used</option><option value="fair">Fair</option>
                </select>
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Length (ft)</Label>
                <Input data-testid="deal-length" type="number" value={form.length_ft} onChange={e => setForm(f => ({...f, length_ft: e.target.value}))} placeholder="24" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Features / Options</Label>
              <textarea data-testid="deal-features" value={form.features} onChange={e => setForm(f => ({...f, features: e.target.value}))}
                placeholder="Solar panels, leveling jacks, outdoor kitchen..." rows={2}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
            <Button data-testid="deal-submit" type="submit" disabled={loading}
              style={{ width: '100%', marginTop: 16, background: accent, color: '#fff', fontWeight: 600, border: 'none', height: 44 }}>
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} /> Analyzing Deal...</> : 'Analyze This Deal'}
            </Button>
          </form>

          {/* Results */}
          {result && !result.raw_response && (
            <div data-testid="deal-results" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Verdict Card */}
              {v && (
                <div style={{ background: v.bg, borderRadius: 14, border: `2px solid ${v.color}40`, padding: 28, textAlign: 'center' }}>
                  <p style={{ fontSize: 40, margin: 0 }}>{v.icon}</p>
                  <p style={{ color: v.color, fontSize: 24, fontWeight: 800, letterSpacing: '0.02em', marginTop: 8 }}>{v.label}</p>
                  {result.confidence && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, background: isDark ? '#1a1a28' : '#fff', padding: '4px 12px', borderRadius: 20 }}>
                      <ShieldCheck size={14} style={{ color: v.color }} />
                      <span style={{ color: textSecondary, fontSize: 13 }}>{result.confidence}% confidence</span>
                    </div>
                  )}
                </div>
              )}

              {/* Price Comparison */}
              <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                  <div style={{ padding: 14, borderRadius: 10, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                    <p style={{ color: textSecondary, fontSize: 11, fontWeight: 500 }}>ASKING PRICE</p>
                    <p style={{ color: textPrimary, fontSize: 20, fontWeight: 800 }}>${(form.asking_price || 0).toLocaleString()}</p>
                  </div>
                  <div style={{ padding: 14, borderRadius: 10, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                    <p style={{ color: textSecondary, fontSize: 11, fontWeight: 500 }}>MARKET VALUE</p>
                    <p style={{ color: textPrimary, fontSize: 20, fontWeight: 800 }}>${(result.estimated_market_value || 0).toLocaleString()}</p>
                  </div>
                  {result.suggested_offer && (
                    <div style={{ padding: 14, borderRadius: 10, background: '#22c55e10', border: '1px solid #22c55e30', gridColumn: 'span 2' }}>
                      <p style={{ color: '#22c55e', fontSize: 11, fontWeight: 600 }}>SUGGESTED OFFER</p>
                      <p style={{ color: textPrimary, fontSize: 24, fontWeight: 800 }}>${result.suggested_offer.toLocaleString()}</p>
                    </div>
                  )}
                </div>
                {r && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <span style={{ display: 'inline-block', padding: '8px 24px', borderRadius: 8, background: `${r.color}15`, color: r.color, fontSize: 15, fontWeight: 700, letterSpacing: '0.03em' }}>
                      Recommendation: {r.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Summary */}
              {result.summary && (
                <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
                  <p style={{ color: textPrimary, fontSize: 14, lineHeight: 1.8, fontWeight: 500 }}>{result.summary}</p>
                </div>
              )}

              {/* Depreciation */}
              {result.depreciation_info && (
                <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
                  <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrendingDown size={16} style={{ color: '#f59e0b' }} /> Depreciation Forecast
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    <DepCard label="In 1 Year" value={result.depreciation_info.value_in_1_year} isDark={isDark} border={border} textPrimary={textPrimary} />
                    <DepCard label="In 3 Years" value={result.depreciation_info.value_in_3_years} isDark={isDark} border={border} textPrimary={textPrimary} />
                    <DepCard label="In 5 Years" value={result.depreciation_info.value_in_5_years} isDark={isDark} border={border} textPrimary={textPrimary} />
                  </div>
                </div>
              )}

              {/* Pros & Cons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {result.pros && (
                  <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
                    <h4 style={{ color: '#22c55e', fontSize: 13, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><ThumbsUp size={14} /> PROS</h4>
                    {result.pros.map((p, i) => <p key={i} style={{ color: textSecondary, fontSize: 13, lineHeight: 1.6, marginTop: i > 0 ? 6 : 0 }}>+ {p}</p>)}
                  </div>
                )}
                {result.cons && (
                  <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
                    <h4 style={{ color: '#ef4444', fontSize: 13, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><ThumbsDown size={14} /> CONS</h4>
                    {result.cons.map((c, i) => <p key={i} style={{ color: textSecondary, fontSize: 13, lineHeight: 1.6, marginTop: i > 0 ? 6 : 0 }}>- {c}</p>)}
                  </div>
                )}
              </div>

              {/* Alternatives */}
              {result.similar_alternatives && result.similar_alternatives.length > 0 && (
                <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
                  <h4 style={{ color: textPrimary, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Consider These Alternatives</h4>
                  {result.similar_alternatives.map((alt, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < result.similar_alternatives.length - 1 ? `1px solid ${border}` : 'none' }}>
                      <div>
                        <p style={{ color: textPrimary, fontSize: 14, fontWeight: 600 }}>{alt.name}</p>
                        <p style={{ color: textSecondary, fontSize: 12 }}>{alt.why}</p>
                      </div>
                      <p style={{ color: '#22c55e', fontWeight: 700, fontSize: 15 }}>${(alt.typical_price || 0).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {result && result.raw_response && (
            <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
              <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{result.raw_response}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DepCard = ({ label, value, isDark, border, textPrimary }) => (
  <div style={{ padding: 12, borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}`, textAlign: 'center' }}>
    <p style={{ color: '#8b8ba3', fontSize: 11, fontWeight: 500 }}>{label}</p>
    <p style={{ color: textPrimary, fontSize: 17, fontWeight: 700, marginTop: 2 }}>${(value || 0).toLocaleString()}</p>
  </div>
);

export default DealAnalyzerPage;
export { DealAnalyzerContent };
